<template>
  <div class="cover-maker">
    <h2>封面制作工具</h2>
    
    <div class="controls">
      <!-- 底图上传 -->
      <div class="control-group">
        <label for="background-image">上传底图:</label>
        <input 
          type="file" 
          id="background-image" 
          accept="image/*" 
          @change="handleImageUpload"
        >
      </div>
      
      <!-- 标题文字 -->
      <div class="control-group">
        <label for="title-text">标题文字:</label>
        <input 
          type="text" 
          id="title-text" 
          v-model="titleText" 
          placeholder="输入标题文字"
        >
      </div>
      
      <!-- 标题背景色 -->
      <div class="control-group">
        <label for="title-bg-color">标题背景色:</label>
        <input 
          type="color" 
          id="title-bg-color" 
          v-model="titleBgColor"
        >
      </div>
      
      <!-- 文字颜色 -->
      <div class="control-group">
        <label for="text-color">文字颜色:</label>
        <input 
          type="color" 
          id="text-color" 
          v-model="textColor"
        >
      </div>
      
      <!-- 字体大小 -->
      <div class="control-group">
        <label for="font-size">字体大小 (px):</label>
        <input 
          type="range" 
          id="font-size" 
          min="12" 
          max="72" 
          v-model.number="fontSize"
        >
        <span>{{ fontSize }}px</span>
      </div>

      <!-- 标题位置 -->
      <div class="control-group">
        <label for="title-position">标题位置:</label>
        <select id="title-position" v-model="titlePosition">
          <option value="top">顶部</option>
          <option value="bottom">底部</option>
          <option value="center">居中</option>
        </select>
      </div>
    </div>
    
    <!-- 预览区域 -->
    <div class="preview-container" ref="previewContainer">
      <div 
        class="cover-preview" 
        :style="coverStyle"
        ref="coverPreview"
      >
        <div class="title-container" :style="titleContainerStyle">
          <h3 :style="titleStyle">{{ titleText || '标题文字' }}</h3>
        </div>
      </div>
    </div>
    
    <!-- 下载按钮 -->
    <button @click="downloadCover" class="download-btn" :disabled="!backgroundImage">
      {{ backgroundImage ? '下载封面' : '请先上传底图' }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'CoverMaker',
  data() {
    return {
      backgroundImage: null,
      originalImageWidth: 0,
      originalImageHeight: 0,
      titleText: '',
      titleBgColor: '#3498db',
      textColor: '#ffffff',
      fontSize: 24,
      titlePosition: 'bottom',
      previewScale: 1
    }
  },
  computed: {
    coverStyle() {
      return {
        backgroundImage: this.backgroundImage ? `url(${this.backgroundImage})` : 'none',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        width: `${this.originalImageWidth * this.previewScale}px`,
        height: `${this.originalImageHeight * this.previewScale}px`,
        position: 'relative',
        transformOrigin: 'top left',
        transform: `scale(${this.previewScale})`
      }
    },
    titleContainerStyle() {
      const style = {
        position: 'absolute',
        left: '0',
        right: '0',
        backgroundColor: this.titleBgColor,
        padding: '10px',
        textAlign: 'center'
      }
      
      if (this.titlePosition === 'top') {
        style.top = '0'
      } else if (this.titlePosition === 'bottom') {
        style.bottom = '0'
      } else {
        style.top = '50%'
        style.transform = 'translateY(-50%)'
      }
      
      return style
    },
    titleStyle() {
      return {
        color: this.textColor,
        fontSize: `${this.fontSize}px`,
        margin: '0',
        padding: '5px 10px',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        maxWidth: '100%'
      }
    }
  },
  methods: {
    handleImageUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.backgroundImage = e.target.result
          
          // 创建一个临时图片对象来获取原始尺寸
          const img = new Image()
          img.onload = () => {
            this.originalImageWidth = img.width
            this.originalImageHeight = img.height
            
            // 计算预览区域的缩放比例
            this.$nextTick(() => {
              this.calculatePreviewScale()
            })
          }
          img.src = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    
    calculatePreviewScale() {
      const container = this.$refs.previewContainer
      const preview = this.$refs.coverPreview
      
      if (!container || !preview) return
      
      // 容器的最大可用宽度和高度
      const maxWidth = container.clientWidth
      const maxHeight = 400 // 固定最大高度，可以根据需要调整
      
      // 计算保持宽高比的缩放比例
      const widthRatio = maxWidth / this.originalImageWidth
      const heightRatio = maxHeight / this.originalImageHeight
      this.previewScale = Math.min(widthRatio, heightRatio, 1)
    },
    
    downloadCover() {
      if (!this.backgroundImage) return
      
      // 创建一个canvas元素，使用原始图片尺寸
      const canvas = document.createElement('canvas')
      canvas.width = this.originalImageWidth
      canvas.height = this.originalImageHeight
      const ctx = canvas.getContext('2d')
      
      // 绘制背景
      const img = new Image()
      img.onload = () => {
        // 绘制背景图
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        
        // 计算标题区域高度（基于字体大小）
        const titleHeight = this.fontSize + 20 // 字体大小 + 上下内边距
        
        // 绘制标题背景
        ctx.fillStyle = this.titleBgColor
        let titleY = 0
        
        if (this.titlePosition === 'bottom') {
          titleY = canvas.height - titleHeight
        } else if (this.titlePosition === 'center') {
          titleY = (canvas.height - titleHeight) / 2
        }
        
        ctx.fillRect(0, titleY, canvas.width, titleHeight)
        
        // 绘制文字
        ctx.fillStyle = this.textColor
        ctx.font = `${this.fontSize}px Arial`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        
        // 计算文字Y位置
        let textY = titleY + (titleHeight / 2)
        
        // 如果标题在底部，稍微调整位置使其看起来更好
        if (this.titlePosition === 'bottom') {
          textY = canvas.height - (titleHeight / 2)
        } else if (this.titlePosition === 'center') {
          textY = titleY + (titleHeight / 2)
        }
        
        // 处理文字过长的情况
        const maxTextWidth = canvas.width * 0.8
        ctx.font = `${this.fontSize}px Arial`
        const textWidth = ctx.measureText(this.titleText || '标题文字').width
        
        if (textWidth > maxTextWidth) {
          // 如果文字太长，缩小字体
          const newFontSize = (maxTextWidth / textWidth) * this.fontSize
          ctx.font = `${newFontSize}px Arial`
        }
        
        ctx.fillText(
          this.titleText || '标题文字', 
          canvas.width / 2, 
          textY
        )
        
        // 触发下载
        this.triggerDownload(canvas)
      }
      img.src = this.backgroundImage
    },
    
    triggerDownload(canvas) {
      const link = document.createElement('a')
      link.download = 'cover.png'
      link.href = canvas.toDataURL('image/png')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
  mounted() {
    window.addEventListener('resize', this.calculatePreviewScale)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculatePreviewScale)
  }
}
</script>

<style scoped>
.cover-maker {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

h2 {
  text-align: center;
  color: #333;
}

.controls {
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.control-group {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.control-group label {
  display: inline-block;
  width: 120px;
  font-weight: bold;
}

.control-group input[type="text"],
.control-group input[type="color"],
.control-group input[type="range"],
.control-group select {
  flex-grow: 1;
}

.control-group input[type="range"] {
  margin-right: 10px;
}

.preview-container {
  width: 100%;
  max-height: 400px;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: auto;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 10px;
  box-sizing: border-box;
}

.cover-preview {
  background-color: #f0f0f0;
  box-shadow: 0 0 5px rgba(0,0,0,0.1);
  transform-origin: top left;
}

.download-btn {
  display: block;
  width: 200px;
  margin: 0 auto;
  padding: 10px 15px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
}

.download-btn:hover:not(:disabled) {
  background-color: #45a049;
}

.download-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.title-container {
  max-width: 100%;
  box-sizing: border-box;
}
</style>